<script lang="ts">
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { getRequest } from "@/Util";

    export let token: string
    export let email: string = ''
    /** Called with the URL to send the user to — the reset also signs them in. */
    export let onSuccess: (redirectTo: string) => void

    let password = ''
    let passwordConfirmation = ''
    let showPassword = false
    let loading = false
    let error = ''
    let fieldErrors: Record<string, string> = {}

    async function submit() {
        error = ''
        fieldErrors = {}
        if (password !== passwordConfirmation) {
            fieldErrors.password_confirmation = 'Passwords do not match.'
            return
        }
        loading = true
        try {
            // ResetPasswordController answers AJAX requests with JSON — without
            // that, a success is a 302 that fetch() follows silently and we would
            // be guessing from whatever the dashboard returned.
            const res = await fetch('/password/reset', {
                ...getRequest(),
                method: 'POST',
                body: JSON.stringify({ token, email, password, password_confirmation: passwordConfirmation }),
            })
            const data = await res.json().catch(() => ({}))

            if (res.ok && data?.success) {
                onSuccess(data.redirect ?? '/my-fitness')
            } else if (data?.errors) {
                const next: Record<string, string> = {}
                for (const [k, msgs] of Object.entries(data.errors as Record<string, string[]>)) {
                    next[k] = msgs[0]
                }
                fieldErrors = next
            } else {
                error = data?.message ?? 'Something went wrong. Please try again.'
            }
        } catch {
            error = 'A network error occurred. Please try again.'
        } finally {
            loading = false
        }
    }
</script>

<div class="space-y-6">
    <div class="space-y-2 text-center">
        <h1 class="text-2xl font-semibold tracking-tight">Set a new password</h1>
        <p class="text-sm text-muted-foreground">Enter and confirm your new password below.</p>
    </div>

    <form on:submit|preventDefault={submit} class="space-y-4">
        {#if error}
            <div class="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {error}
            </div>
        {/if}

        <div class="space-y-1.5">
            <Label for="reset-email">Email address</Label>
            <Input
                id="reset-email"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
                bind:value={email}
                disabled={loading}
                required
            />
            {#if fieldErrors.email}
                <p class="text-xs text-destructive">{fieldErrors.email}</p>
            {/if}
        </div>

        <div class="space-y-1.5">
            <Label for="reset-password">New password</Label>
            <div class="relative">
                <Input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    autocomplete="new-password"
                    placeholder="••••••••"
                    bind:value={password}
                    disabled={loading}
                    required
                    minlength={6}
                    class="pr-10"
                />
                <button
                    type="button"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    on:click={() => (showPassword = !showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                    {#if showPassword}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21"/></svg>
                    {:else}
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    {/if}
                </button>
            </div>
            {#if fieldErrors.password}
                <p class="text-xs text-destructive">{fieldErrors.password}</p>
            {/if}
        </div>

        <div class="space-y-1.5">
            <Label for="reset-confirm">Confirm new password</Label>
            <Input
                id="reset-confirm"
                type={showPassword ? 'text' : 'password'}
                autocomplete="new-password"
                placeholder="••••••••"
                bind:value={passwordConfirmation}
                disabled={loading}
                required
                class={fieldErrors.password_confirmation ? 'border-destructive' : ''}
            />
            {#if fieldErrors.password_confirmation}
                <p class="text-xs text-destructive">{fieldErrors.password_confirmation}</p>
            {/if}
        </div>

        <Button type="submit" class="w-full" disabled={loading}>
            {#if loading}
                <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            {/if}
            Reset password
        </Button>
    </form>
</div>
